import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { SalaryType, WorkerEmploymentType } from "model/Salary";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
  StaffProfileData,
  StaffProfileEmploymentCurrent,
} from "services/api/company/staff.service";
import { sanitizeNonNegativeDecimalInput } from "utils/input.normalize.logic";
import { toEmploymentTypeDescription } from "web-apps/company/components/common/employmentType/employmentType.logic";
import {
  calculateMonthlyPayoutSalary,
  formatMonthlySalary,
} from "web-apps/company/components/common/salary/monthlyPayoutSalary.logic";
import { getEmploymentFieldVisibility } from "web-apps/company/components/staff/components/shared/staffEmploymentFieldVisibility.logic";
import type { EmploymentUiRules } from "web-apps/company/components/staff/components/shared/staffEmploymentRules.logic";
import { StaffEmploymentEndDateField } from "web-apps/company/components/staff/components/shared/staffEmploymentEndDateField.component";
import { EmploymentTypeDescription } from "web-apps/company/components/staff/components/shared/staffEmploymentType.styled";
import { createIntegerInputHandlers } from "web-apps/company/components/staff/components/shared/staffNumericInput.logic";
import { getSelectedOptionLabel } from "web-apps/company/components/staff/components/shared/staffPanel.options.logic";
import {
  FieldCard,
  FieldLabel,
  FieldValue,
  NoEmploymentBanner,
  NoEmploymentBannerBody,
  Section,
  SectionActionRow,
  SectionBox,
  SectionTitle,
  SalaryFieldsGrid,
  TemplateFieldsGrid,
  TwoColumnFieldsGrid,
  UpcomingEmploymentBanner,
  UpcomingEmploymentBannerBody,
} from "web-apps/company/components/staff/components/profilePanel/layout/profilePanel.styled";
import {
  EmployeeNumberFieldCard,
  RoleFieldCard,
} from "web-apps/company/components/staff/components/profilePanel/domains/employment/profilePanel.employmentSection.styled";
import type {
  EmploymentDetailsForm,
  EmploymentOptions,
} from "web-apps/company/components/staff/components/profilePanel/profilePanel.contract";
import { getEmploymentFieldValidationErrors } from "web-apps/company/components/staff/components/profilePanel/state/profilePanel.state.validation.logic";
import { formatDateLabelInStockholm } from "web-apps/company/utils/timeAndDate";

interface ProfilePanelEmploymentSectionProps {
  readonly currentEmployment: StaffProfileEmploymentCurrent | null;
  readonly editedEmploymentDetails: EmploymentDetailsForm;
  readonly employmentOptions: EmploymentOptions;
  readonly employmentRules: EmploymentUiRules;
  readonly employmentTemplates: EmploymentTemplateListItem[];
  readonly isCreateMode: boolean;
  readonly isEditMode: boolean;
  readonly isLoadingEmployeeNumber: boolean;
  readonly isLoadingEmploymentTemplates: boolean;
  readonly isSaving: boolean;
  readonly profile: StaffProfileData;
  readonly selectedEmploymentTemplateId: string;
  readonly selectedTemplateSalaryId: string;
  readonly showNoEmploymentBanner: boolean;
  readonly templateSalaries: EmploymentTemplateSalary[];
  readonly upcomingEmploymentStartDate: string | null;
  readonly onChangePaymentMethodClick: () => void;
  readonly onChangeSalaryClick: () => void;
  readonly onChangeTaxClick: () => void;
  readonly onChangeToPermanentClick: () => void;
  readonly onEmploymentFieldChange: <
    TField extends keyof EmploymentDetailsForm,
  >(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
  readonly onFetchEmployeeNumber: () => void;
  readonly onSelectEmploymentTemplate: (templateId: string) => void;
  readonly onSelectTemplateSalary: (salaryId: string) => void;
}

export const ProfilePanelEmploymentSection = (
  props: ProfilePanelEmploymentSectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  const toMandatoryLabel = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;
  const salaryTypeInForm = props.isEditMode
    ? props.editedEmploymentDetails.salaryType
    : props.currentEmployment?.salaryType;
  const hasUpcomingEmployment = props.upcomingEmploymentStartDate != null;
  const paymentMethod = props.profile.paymentMethods[0] ?? null;
  // Field visibility by employment type is applied only while editing; the
  // read-only view keeps showing whatever values exist.
  const fieldVisibility = getEmploymentFieldVisibility(
    props.editedEmploymentDetails.employmentType,
  );
  const employmentTypeDescription = toEmploymentTypeDescription({
    employmentType: props.editedEmploymentDetails.employmentType,
    translate: t,
  });
  const shouldShowWorkPercentage =
    salaryTypeInForm === SalaryType.Monthly &&
    (!props.isEditMode || fieldVisibility.showSalaryType);
  const salaryTypeLabel = getSelectedOptionLabel({
    options: props.employmentOptions.salaryOptions,
    value: props.editedEmploymentDetails.salaryType,
  });
  const paymentTypeLabel = getSelectedOptionLabel({
    options: props.employmentOptions.paymentOptions,
    value: props.editedEmploymentDetails.paymentType,
  });
  const noticeDaysHandlers = createIntegerInputHandlers(
    props.editedEmploymentDetails.noticeDays,
    (value) => props.onEmploymentFieldChange("noticeDays", value),
  );
  const vacationDaysHandlers = createIntegerInputHandlers(
    props.editedEmploymentDetails.vacationDays,
    (value) => props.onEmploymentFieldChange("vacationDays", value),
  );
  const validationErrors = getEmploymentFieldValidationErrors(
    props.editedEmploymentDetails,
  );
  const payoutSalary = calculateMonthlyPayoutSalary({
    salary: props.currentEmployment?.salary,
    salaryType: props.currentEmployment?.salaryType,
    workPercentage: props.currentEmployment?.workPercentage,
  });
  const shouldShowPayoutSalary = !props.isCreateMode && payoutSalary != null;

  return (
    <>
      <Section>
        <SectionTitle>{t("Staff.ProfilePanelEmployment")}</SectionTitle>
        {props.upcomingEmploymentStartDate != null && (
          <UpcomingEmploymentBanner>
            <UpcomingEmploymentBannerBody>
              {t("Staff.ProfilePanelUpcomingEmploymentStartsFrom", {
                date: formatDateLabelInStockholm(
                  props.upcomingEmploymentStartDate,
                  "sv-SE",
                ),
              })}
            </UpcomingEmploymentBannerBody>
          </UpcomingEmploymentBanner>
        )}
        {!hasUpcomingEmployment && props.showNoEmploymentBanner && (
          <NoEmploymentBanner>
            <NoEmploymentBannerBody>
              {t("Staff.ProfilePanelNoActiveOrUpcomingEmployment")}
            </NoEmploymentBannerBody>
          </NoEmploymentBanner>
        )}
        <SectionBox>
          {props.isCreateMode && (
            <TemplateFieldsGrid>
              <FieldCard>
                <Dropdown
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldTemplate")}
                  options={[
                    {
                      value: "",
                      label: t("Staff.AddStaffPanelTemplateCustom"),
                    },
                    ...props.employmentTemplates.map((template) => ({
                      value: String(template.id),
                      label: template.name ?? "",
                    })),
                  ]}
                  value={props.selectedEmploymentTemplateId}
                  disabled={
                    props.isLoadingEmploymentTemplates ||
                    props.employmentTemplates.length === 0
                  }
                  onChange={props.onSelectEmploymentTemplate}
                />
              </FieldCard>
              <FieldCard>
                <Dropdown
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldTemplateSalary")}
                  placeholder={t(
                    "Staff.AddStaffPanelPlaceholderTemplateSalary",
                  )}
                  options={[
                    {
                      value: "",
                      label: t("Staff.AddStaffPanelTemplateCustom"),
                    },
                    ...props.templateSalaries.map((salary) => ({
                      value: String(salary.id),
                      label: salary.name ?? "",
                    })),
                  ]}
                  value={props.selectedTemplateSalaryId}
                  disabled={props.templateSalaries.length === 0}
                  onChange={props.onSelectTemplateSalary}
                />
              </FieldCard>
            </TemplateFieldsGrid>
          )}
          <TwoColumnFieldsGrid>
            <FieldCard>
              {props.isEditMode ? (
                <>
                  <Dropdown
                    fullWidth
                    label={toMandatoryLabel(t("Staff.EmploymentType"))}
                    options={props.employmentOptions.employmentOptions}
                    value={props.editedEmploymentDetails.employmentType}
                    onChange={(employmentType: string) =>
                      props.onEmploymentFieldChange(
                        "employmentType",
                        employmentType,
                      )
                    }
                  />
                  {employmentTypeDescription && (
                    <EmploymentTypeDescription $hasTopMargin>
                      {employmentTypeDescription}
                    </EmploymentTypeDescription>
                  )}
                </>
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.employmentType
                      ? t(
                          `Staff.employmentType${props.currentEmployment.employmentType}`,
                        )
                      : "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.EmploymentType")}</FieldLabel>
                </>
              )}
            </FieldCard>
            {(!props.isEditMode || fieldVisibility.showEmployeeCategory) && (
              <FieldCard>
                {props.isEditMode ? (
                  <Dropdown
                    fullWidth
                    label={toMandatoryLabel(
                      t("OrganizationalStructure.EmployeeCategory"),
                    )}
                    options={props.employmentOptions.employeeCategoryOptions}
                    value={props.editedEmploymentDetails.employeeCategory}
                    onChange={(employeeCategory: string) =>
                      props.onEmploymentFieldChange(
                        "employeeCategory",
                        employeeCategory,
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.employeeCategory &&
                      props.currentEmployment.employeeCategory !== "Undefined"
                        ? (props.employmentOptions.employeeCategoryLabelByValue[
                            props.currentEmployment.employeeCategory
                          ] ?? props.currentEmployment.employeeCategory)
                        : "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldStaffType")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            )}
            <EmployeeNumberFieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  type="number"
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldEmployeeNumber"),
                  )}
                  value={props.editedEmploymentDetails.employeeNumber}
                  hasError={validationErrors.employeeNumber != null}
                  errorText={
                    validationErrors.employeeNumber == null
                      ? undefined
                      : t(validationErrors.employeeNumber.translationKey)
                  }
                  iconName="v2/reload"
                  iconSize="18px"
                  iconAriaLabel={t(
                    "Staff.AddStaffPanelActionReloadEmployeeNumber",
                  )}
                  iconTabbable
                  onIconClick={props.onFetchEmployeeNumber}
                  disabled={props.isLoadingEmployeeNumber}
                  onChange={(employeeNumber) =>
                    props.onEmploymentFieldChange(
                      "employeeNumber",
                      employeeNumber,
                    )
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.employeeNumber ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEmployeeNumber")}
                  </FieldLabel>
                </>
              )}
            </EmployeeNumberFieldCard>
            <RoleFieldCard>
              {props.isEditMode ? (
                <InputField
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"))}
                  value={props.editedEmploymentDetails.role}
                  hasError={validationErrors.role != null}
                  errorText={
                    validationErrors.role == null
                      ? undefined
                      : t(validationErrors.role.translationKey)
                  }
                  onChange={(role) =>
                    props.onEmploymentFieldChange("role", role)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {props.currentEmployment?.role?.trim() || "-"}
                  </FieldValue>
                  <FieldLabel>{t("Staff.AddStaffPanelFieldRole")}</FieldLabel>
                </>
              )}
            </RoleFieldCard>
            {shouldShowWorkPercentage && (
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={toMandatoryLabel(
                      `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                    )}
                    value={props.editedEmploymentDetails.workPercentage}
                    hasError={validationErrors.workPercentage != null}
                    errorText={
                      validationErrors.workPercentage == null
                        ? undefined
                        : t(validationErrors.workPercentage.translationKey)
                    }
                    onChange={(workPercentage) =>
                      props.onEmploymentFieldChange(
                        "workPercentage",
                        workPercentage,
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.profile.workPercentage == null
                        ? "-"
                        : `${Math.round(props.profile.workPercentage * 100)}%`}
                    </FieldValue>
                    <FieldLabel>
                      {`${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            )}
            <FieldCard>
              {props.isEditMode ? (
                <DatePicker
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldStartDate"),
                  )}
                  value={props.editedEmploymentDetails.startDate}
                  allowClear={false}
                  onChange={(startDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("startDate", startDate)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.currentEmployment?.startDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldStartDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
            <FieldCard>
              {props.isEditMode ? (
                <StaffEmploymentEndDateField
                  allowClear={!props.employmentRules.isEndDateRequiredOnSave}
                  employmentType={props.editedEmploymentDetails.employmentType}
                  endDate={props.editedEmploymentDetails.endDate}
                  label={
                    props.employmentRules.isEndDateRequiredOnSave
                      ? toMandatoryLabel(t("Staff.AddStaffPanelFieldEndDate"))
                      : t("Staff.AddStaffPanelFieldEndDate")
                  }
                  startDate={props.editedEmploymentDetails.startDate}
                  onChange={(endDate: LocalDate | null) =>
                    props.onEmploymentFieldChange("endDate", endDate)
                  }
                />
              ) : (
                <>
                  <FieldValue>
                    {formatDateLabelInStockholm(
                      props.currentEmployment?.endDate ?? "",
                      "sv-SE",
                    )}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldEndDate")}
                  </FieldLabel>
                </>
              )}
            </FieldCard>
          </TwoColumnFieldsGrid>
          {!props.isEditMode &&
            props.currentEmployment?.employmentType ===
              WorkerEmploymentType.INTRODUCTORY && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeToPermanentButton")}
                  size="small"
                  disabled={props.isSaving}
                  onClick={props.onChangeToPermanentClick}
                />
              </SectionActionRow>
            )}
        </SectionBox>
      </Section>

      {(!props.isEditMode || fieldVisibility.showSalary) && (
        <Section>
          <SectionTitle>{t("Staff.ProfilePanelSalary")}</SectionTitle>
          <SectionBox>
            <SalaryFieldsGrid $hasPayoutSalary={shouldShowPayoutSalary}>
              <FieldCard>
                {props.isEditMode && props.isCreateMode ? (
                  props.employmentOptions.salaryOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldSalaryModel"),
                      )}
                      value={salaryTypeLabel}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldSalaryModel"),
                      )}
                      options={props.employmentOptions.salaryOptions}
                      value={props.editedEmploymentDetails.salaryType}
                      onChange={(salaryType: string) =>
                        props.onEmploymentFieldChange("salaryType", salaryType)
                      }
                    />
                  )
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.salaryType
                        ? t(
                            `Staff.salaryType${props.currentEmployment.salaryType}`,
                          )
                        : "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldSalaryModel")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode && props.isCreateMode ? (
                  <InputField
                    fullWidth
                    label={toMandatoryLabel(
                      t("Staff.AddStaffPanelFieldSalaryAmount"),
                    )}
                    value={props.editedEmploymentDetails.salary}
                    onChange={(salary) =>
                      props.onEmploymentFieldChange(
                        "salary",
                        sanitizeNonNegativeDecimalInput(salary),
                      )
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.salary == null
                        ? "-"
                        : (() => {
                            const displayValue = String(
                              props.currentEmployment.salary,
                            ).trim();
                            return displayValue.length > 0
                              ? `${displayValue} kr`
                              : "-";
                          })()}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldSalaryAmount")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              {shouldShowPayoutSalary && payoutSalary != null && (
                <FieldCard>
                  <FieldValue>{formatMonthlySalary(payoutSalary)}</FieldValue>
                  <FieldLabel>{t("Salary.PayoutSalary")}</FieldLabel>
                </FieldCard>
              )}
              <FieldCard>
                {props.isEditMode && props.isCreateMode ? (
                  props.employmentOptions.paymentOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldPaymentSolution"),
                      )}
                      value={paymentTypeLabel}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldPaymentSolution"),
                      )}
                      options={props.employmentOptions.paymentOptions}
                      value={props.editedEmploymentDetails.paymentType}
                      onChange={(paymentType: string) =>
                        props.onEmploymentFieldChange(
                          "paymentType",
                          paymentType,
                        )
                      }
                    />
                  )
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.paymentType
                        ? props.currentEmployment.paymentType === "None"
                          ? t("System.OwnSalaryManagement")
                          : "SimflexityPay"
                        : "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldPaymentSolution")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            </SalaryFieldsGrid>
            {!props.isEditMode && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeSalary")}
                  size="small"
                  onClick={props.onChangeSalaryClick}
                />
              </SectionActionRow>
            )}
          </SectionBox>
        </Section>
      )}

      {(!props.isEditMode || fieldVisibility.showTaxTable) && (
        <Section>
          <SectionTitle>{t("Salary.TaxProcent")}</SectionTitle>
          <SectionBox>
            <TwoColumnFieldsGrid>
              <FieldCard>
                <>
                  <FieldValue>
                    {props.currentEmployment?.taxTable ?? "-"}
                  </FieldValue>
                  <FieldLabel>
                    {t("Staff.AddStaffPanelFieldTaxTable")}
                  </FieldLabel>
                </>
              </FieldCard>
            </TwoColumnFieldsGrid>
            {!props.isEditMode && (
              <SectionActionRow>
                <SecondaryButton
                  label={t("Staff.ProfilePanelChangeTax")}
                  size="small"
                  onClick={props.onChangeTaxClick}
                />
              </SectionActionRow>
            )}
          </SectionBox>
        </Section>
      )}

      <Section>
        <SectionTitle>{t("Staff.PaymentMethodSectionTitle")}</SectionTitle>
        <SectionBox>
          <TwoColumnFieldsGrid>
            <FieldCard>
              <>
                <FieldValue>{paymentMethod?.value?.trim() || "-"}</FieldValue>
                <FieldLabel>{t("Staff.PaymentMethodValue")}</FieldLabel>
              </>
            </FieldCard>
            <FieldCard>
              <>
                <FieldValue>
                  {paymentMethod?.type
                    ? t(`Staff.PaymentMethod${paymentMethod.type}`)
                    : "-"}
                </FieldValue>
                <FieldLabel>{t("Staff.PaymentMethodType")}</FieldLabel>
              </>
            </FieldCard>
          </TwoColumnFieldsGrid>
          {!props.isEditMode && (
            <SectionActionRow>
              <SecondaryButton
                label={t("Staff.ProfilePanelChangePaymentMethod")}
                size="small"
                onClick={props.onChangePaymentMethodClick}
              />
            </SectionActionRow>
          )}
        </SectionBox>
      </Section>

      {(!props.isEditMode || fieldVisibility.showContractTerms) && (
        <Section>
          <SectionTitle>{t("Staff.ProfilePanelContractTerms")}</SectionTitle>
          <SectionBox>
            <TwoColumnFieldsGrid>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    label={t(
                      "Staff.AddStaffPanelFieldCollectiveSalaryAgreement",
                    )}
                    value={props.editedEmploymentDetails.agreement}
                    hasError={validationErrors.agreement != null}
                    errorText={
                      validationErrors.agreement == null
                        ? undefined
                        : t(validationErrors.agreement.translationKey)
                    }
                    onChange={(agreement) =>
                      props.onEmploymentFieldChange("agreement", agreement)
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.agreement?.trim() || "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldCollectiveSalaryAgreement")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    value={props.editedEmploymentDetails.noticeDays}
                    hasError={validationErrors.noticeDays != null}
                    errorText={
                      validationErrors.noticeDays == null
                        ? undefined
                        : t(validationErrors.noticeDays.translationKey)
                    }
                    onChange={noticeDaysHandlers.onChange}
                    onBlur={noticeDaysHandlers.onBlur}
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.noticeDays ?? "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldVacationDays")}
                    value={props.editedEmploymentDetails.vacationDays}
                    hasError={validationErrors.vacationDays != null}
                    errorText={
                      validationErrors.vacationDays == null
                        ? undefined
                        : t(validationErrors.vacationDays.translationKey)
                    }
                    onChange={vacationDaysHandlers.onChange}
                    onBlur={vacationDaysHandlers.onBlur}
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.vacationDays ?? "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldVacationDays")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
              <FieldCard>
                {props.isEditMode ? (
                  <InputField
                    fullWidth
                    label={t("Staff.AddStaffPanelFieldWorkPlace")}
                    value={props.editedEmploymentDetails.workplace}
                    hasError={validationErrors.workplace != null}
                    errorText={
                      validationErrors.workplace == null
                        ? undefined
                        : t(validationErrors.workplace.translationKey)
                    }
                    onChange={(workplace) =>
                      props.onEmploymentFieldChange("workplace", workplace)
                    }
                  />
                ) : (
                  <>
                    <FieldValue>
                      {props.currentEmployment?.workplace?.trim() || "-"}
                    </FieldValue>
                    <FieldLabel>
                      {t("Staff.AddStaffPanelFieldWorkPlace")}
                    </FieldLabel>
                  </>
                )}
              </FieldCard>
            </TwoColumnFieldsGrid>
          </SectionBox>
        </Section>
      )}
    </>
  );
};
